/**
 * Token reservation (5 days), as the admin panel shows it (backend: utils/tokenReservation).
 *
 * ACTIVE   - tokens held, the clock is running; the investment is cancelled within 5 minutes
 *            of expiry
 * SECURED  - the investor reached the funding page or paid; tokens held, no auto-cancel
 * RELEASED - the investment was cancelled and the tokens released
 *
 * The badge follows the investment: nothing once it is completed (the tokens were delivered),
 * nothing for an investment cancelled without a released reservation, and "secured" once the
 * investor has paid even if the reservation was never marked secured.
 */

export interface ReservationFields {
  status?: string | null;
  reservationStatus?: string | null;
  reservedAt?: string | null;
  reservationExpiresAt?: string | null;
  reservationSecuredAt?: string | null;
  reservationReleasedAt?: string | null;
}

/** Before the investor says they have paid: the only statuses the reservation clock applies to. */
const AWAITING_FUNDING = [
  'DOCUMENT_PENDING_FOR_SIGNING',
  'PENDING',
  'DOCUMENT_SIGNED',
];
const ENDED_UNFUNDED = ['CANCELLED', 'EXPIRED', 'FAILED'];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

/** A short label and a tone, or null when there is no reservation to show. */
export function reservationSummary(
  inv: ReservationFields,
  now = Date.now(),
): {
  label: string;
  detail: string;
  tone: 'active' | 'secured' | 'released' | 'overdue';
} | null {
  const status = inv.reservationStatus;
  if (!status) return null;
  const investmentStatus = inv.status ?? '';

  // Completed: the reserved tokens went to the investor; nothing is reserved any more.
  if (investmentStatus === 'COMPLETED') return null;

  if (status === 'RELEASED') {
    return {
      label: 'Reservation released',
      detail: `Released ${inv.reservationReleasedAt ? fmt(inv.reservationReleasedAt) : ''}; investment cancelled, tokens released.`,
      tone: 'released',
    };
  }
  // Cancelled some other way and not yet settled by the daily job: nothing is held.
  if (ENDED_UNFUNDED.includes(investmentStatus)) return null;

  // Secured, or the investor has paid (the clock no longer applies).
  if (status === 'SECURED' || !AWAITING_FUNDING.includes(investmentStatus)) {
    return {
      label: 'Reserved · secured',
      detail:
        status === 'SECURED' && inv.reservationSecuredAt
          ? `Secured ${fmt(inv.reservationSecuredAt)}. Tokens held until delivered or cancelled.`
          : 'The investor has paid. Tokens held until delivered or cancelled.',
      tone: 'secured',
    };
  }
  if (!inv.reservationExpiresAt) {
    return { label: 'Reserved', detail: '', tone: 'active' };
  }
  const msLeft = new Date(inv.reservationExpiresAt).getTime() - now;
  if (msLeft <= 0) {
    return {
      label: 'Reserved · expired',
      detail: `Expired ${fmt(inv.reservationExpiresAt)}; cancelled automatically within a few minutes.`,
      tone: 'overdue',
    };
  }
  const left =
    msLeft < DAY_MS
      ? `${Math.ceil(msLeft / HOUR_MS)}h`
      : `${Math.ceil(msLeft / DAY_MS)} day${Math.ceil(msLeft / DAY_MS) === 1 ? '' : 's'}`;
  return {
    label: `Reserved · ${left} left`,
    detail: `Reserved ${inv.reservedAt ? fmt(inv.reservedAt) : ''} until ${fmt(inv.reservationExpiresAt)}.`,
    tone: 'active',
  };
}

export const RESERVATION_TONE: Record<string, string> = {
  active: 'border-amber-200 bg-amber-50 text-amber-700',
  secured: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  released: 'border-slate-200 bg-slate-50 text-slate-600',
  overdue: 'border-rose-200 bg-rose-50 text-rose-700',
};
